import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Checkbox, Upload, Input as AntInput } from "antd";
import { AppModal, Input } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { Select } from "@/shared/components/select";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { cn } from "@/shared/lib/utils";
import {
  changePatientPasswordApi,
  deletePatientAccountApi,
  requestPatientEmailChangeApi,
  requestPatientPhoneVerificationApi,
  updatePatientProfileApi,
  uploadPatientAvatarApi,
  verifyPatientEmailChangeApi,
  verifyPatientPhoneVerificationApi,
  type PatientProfile,
} from "@/portals/patient/api/patientProfileApi";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { InputOTP } from "@/shared/components/otp-input";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { toastError, toastSuccess, toastWarning } from "@/shared/lib/toast";
import type { UserProfile } from "@/shared/types";
import { OTP_LENGTH, OTP_VALIDITY_SECONDS } from "../auth/otpConfig";
import {
  saveEmailChangeFlowState,
  clearEmailChangeFlowState,
  getResumableEmailChangeSession,
} from "./emailChangeFlowState";

interface ProfilePageProps {
  profile: UserProfile;
}

interface ProfileForm {
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  email: string;
  gender: UserProfile["gender"];
  dateOfBirth: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const BRAND_RED_SOFT = "!bg-[#E4002B]/50 hover:!bg-[#E4002B]/60";
const BRAND_RED_SUBTLE = "!bg-[#E4002B]/20 !text-[#E4002B] hover:!bg-[#E4002B]/25";
const BRAND_RED_TEXT = "text-[#E4002B]";
const BRAND_RED_BORDER = "border-[#E4002B]/50";

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function formatMonthYear(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(parsed);
}

function formatDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(parsed);
}

function formatMinutesSeconds(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const US_CANADA_PHONE_DIGITS = 10;

// Strips to plain digits as the patient types (no parens/dashes), and also handles a
// stored E.164 value (+1XXXXXXXXXX) so the same helper covers both live input and read display.
function formatUsCanadaPhone(rawValue: string | null | undefined): string {
  if (!rawValue) return "";
  let digits = rawValue.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, US_CANADA_PHONE_DIGITS);
}

// US/Canada (NANP): area code and exchange code must each start 2-9 (0/1 are reserved
// for special routing) — mirrors the backend's ^\+1[2-9]\d{2}[2-9]\d{6}$ exactly, so an
// obviously-invalid number is caught here instead of costing a round trip to a 400.
const NANP_PATTERN = /^\+1[2-9]\d{2}[2-9]\d{6}$/;

// Returns E.164 (+1XXXXXXXXXX) for the backend/Twilio, or null if what's been entered
// isn't a complete, valid US/Canada number.
function toE164UsCanada(rawValue: string | null | undefined): string | null {
  if (!rawValue) return null;
  const digits = rawValue.replace(/\D/g, "");
  let candidate: string | null = null;
  if (digits.length === US_CANADA_PHONE_DIGITS) candidate = `+1${digits}`;
  else if (digits.length === 11 && digits.startsWith("1")) candidate = `+${digits}`;
  return candidate && NANP_PATTERN.test(candidate) ? candidate : null;
}

function genderLabel(gender: UserProfile["gender"]) {
  const labels: Record<UserProfile["gender"], string> = {
    male: "Male",
    female: "Female",
    other: "Other",
    prefer_not_to_say: "Undisclosed",
  };
  return labels[gender];
}

function ProfileField({
  icon,
  label,
  value,
  placeholderValue,
  name,
  editing,
  wide = false,
  type = "text",
  prefix,
  suffix,
  // Whether there's a value to show `suffix` for while editing — defaults to the
  // (possibly blanked-for-editing) `value` itself, but callers whose edit mode clears
  // the draft to show a placeholder (e.g. phone) should pass the real effective value's
  // presence here instead, so the suffix doesn't disappear just because the draft is empty.
  hasValue,
  // Rendered centered, full-width, in place of the value+suffix row when there's no
  // value yet and the field isn't being edited — e.g. "Add Phone Number" instead of a
  // lone "Verify" button floating next to an empty box.
  emptyAction,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  placeholderValue?: string;
  name: keyof ProfileForm;
  editing: boolean;
  wide?: boolean;
  type?: "text" | "email" | "tel" | "date" | "select";
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  hasValue?: boolean;
  emptyAction?: React.ReactNode;
  onChange: (name: keyof ProfileForm, value: string) => void;
}) {
  const showEmptyAction = !editing && !value.trim() && Boolean(emptyAction);
  const hasSuffixValue = hasValue ?? Boolean(value.trim());

  return (
    <label className={wide ? "md:col-span-2" : ""}>
      <span className="mb-2 flex items-center gap-2 text-sm font-medium text-[#111827]">
        {icon}
        {label}
      </span>
      {/* AntD's borderless Input/Select paint their own focus-visible outline in this
          same brand red so keyboard/click focus stays visible on an otherwise-bare
          control — redundant here since this wrapper already carries a full red
          border for the "editing" state, so the two stack into a doubled-border look. */}
      <div className={`msq-field-shell flex h-11 items-center rounded-lg border ${editing ? `${BRAND_RED_BORDER} bg-white` : "border-[#DADDE5] bg-[#F1F2F4]"} px-3 text-sm text-[#111827] [&_:focus-visible]:!outline-none`}>
        {editing && type === "select" ? (
          <Select
            variant="borderless"
            value={value}
            onChange={(next) => onChange(name, next)}
            className="!min-w-0 !flex-1"
            options={[
              { value: "female", label: "Female" },
              { value: "male", label: "Male" },
              { value: "other", label: "Other" },
              { value: "prefer_not_to_say", label: "Undisclosed" },
            ]}
          />
        ) : editing && type === "date" ? (
          <CalendarDateField
            value={value}
            placeholder={placeholderValue ? formatDate(placeholderValue) : "Date of birth"}
            onChange={(nextValue) => onChange(name, nextValue)}
            disablePast={false}
            disableToday
            bare
          />
        ) : editing ? (
          <>
            {prefix}
            <Input
              variant="borderless"
              type={type}
              value={value}
              placeholder={placeholderValue}
              onChange={(event) => onChange(name, event.target.value)}
              className="!min-w-0 !flex-1 !p-0 placeholder:!text-[#7A7F8D]"
            />
            {hasSuffixValue && suffix}
          </>
        ) : showEmptyAction ? (
          <div className="flex w-full items-center justify-center">{emptyAction}</div>
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-[#7A7F8D]">
              {type === "date"
                ? value ? formatDate(value) : placeholderValue
                : type === "select" ? genderLabel(value as UserProfile["gender"]) : value}
            </span>
            {suffix}
          </>
        )}
      </div>
    </label>
  );
}

function PasswordField({
  placeholder,
  value,
  onChange,
  visible,
  onToggleVisible,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  visible: boolean;
  onToggleVisible: () => void;
}) {
  return (
    <div className="msq-field-shell flex h-12 items-center gap-3 rounded-full border border-[#DADDE5] bg-white px-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F1F2F4] text-[#7A7F8D]">
        <Icon icon="lucide:key-round" className="icon-muted h-3.5 w-3.5" />
      </span>
      <div className="h-6 w-px shrink-0 bg-[#DADDE5]" />
      <Input
        variant="borderless"
        type={visible ? "text" : "password"}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="!min-w-0 !flex-1 !p-0 !text-sm !text-[#111827] placeholder:!text-[#7A7F8D]"
      />
      <Button
        variant="link"
        onClick={onToggleVisible}
        className="!h-auto !shrink-0 !p-0 !text-[#7A7F8D] hover:!text-[#111827]"
        aria-label={visible ? "Hide password" : "Show password"}
      >
        {visible ? <Icon icon="lucide:eye-off" className="icon-muted h-4 w-4" /> : <Icon icon="lucide:eye" className="icon-muted h-4 w-4" />}
      </Button>
    </div>
  );
}

function profileToForm(profile: PatientProfile): ProfileForm {
  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    address: profile.address ?? "",
    phone: profile.phone ?? "",
    email: profile.email,
    gender: profile.gender ?? "prefer_not_to_say",
    dateOfBirth: profile.dateOfBirth ?? "",
  };
}

export function ProfilePage({ profile: _fallbackProfile }: ProfilePageProps) {
  const signOut = usePatientAuthStore((s) => s.signOut);
  const { profile: backendProfile, setProfile, avatarSrc: persistedAvatarSrc } = usePatientProfile();
  const initialProfile = profileToForm(backendProfile);

  const [savedProfile, setSavedProfile] = useState<ProfileForm>(initialProfile);
  const [draftProfile, setDraftProfile] = useState<ProfileForm>(initialProfile);
  const [memberSince, setMemberSince] = useState(backendProfile.memberSince);
  const [pendingEmail, setPendingEmail] = useState<string | null>(backendProfile.pendingEmail);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangePassword, setEmailChangePassword] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(backendProfile.phoneVerified);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneCountry, setPhoneCountry] = useState<"US" | "CA">("US");
  // The exact E.164 value the OTP was sent to — Twilio Verify ties the pending code to
  // this phone string, so verify must reuse it rather than re-deriving from savedProfile
  // (which could theoretically change between send and verify).
  const [verifyingPhone, setVerifyingPhone] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"personal" | "security">("personal");
  // Never auto-open on mount, even if a pendingEmail already exists server-side — this
  // modal should only appear in direct response to the patient clicking "Change" below.
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showEmailOtp, setShowEmailOtp] = useState(false);
  const [showEmailChangePassword, setShowEmailChangePassword] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isRequestingEmail, setIsRequestingEmail] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isRequestingPhone, setIsRequestingPhone] = useState(false);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  const [emailRetrySeconds, setEmailRetrySeconds] = useState(0);
  const [phoneRetrySeconds, setPhoneRetrySeconds] = useState(0);
  // How long the currently-pending phone OTP has left before it expires — seeded from
  // the backend's expiresInSeconds so this can't drift out of sync with the server's
  // actual PHONE_OTP_EXPIRE_SECONDS window.
  const [phoneOtpExpirySeconds, setPhoneOtpExpirySeconds] = useState(0);
  // Same idea for the email-change OTP, seeded from the backend's expiresInSeconds
  // (falls back to OTP_VALIDITY_SECONDS if an older backend build omits it).
  const [emailOtpExpirySeconds, setEmailOtpExpirySeconds] = useState(0);
  // The exact password used for the last successful email-change OTP send — kept only
  // in memory, never persisted (sessionStorage must never hold a plaintext password).
  // Resuming that still-active OTP session instead of sending a new one is only allowed
  // when the patient re-submits this exact same password; any change (or a page refresh,
  // which naturally resets this to null) falls back to a real new send.
  const [resumableEmailChangePassword, setResumableEmailChangePassword] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  // Local blob preview of a newly-selected-but-not-yet-saved file only — null means
  // "show the persisted avatar" (persistedAvatarSrc from context, fetched once there
  // and shared with the header/sidebar instead of each fetching it independently).
  const [localAvatarPreview, setLocalAvatarPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);

  const fullName = `${savedProfile.firstName} ${savedProfile.lastName}`.trim();
  // Whatever's currently in the Phone Number box — the draft value while it's being
  // edited, so the verify panel/message stay in sync if the patient changes it there.
  const activePhoneRaw = editing && draftProfile.phone.trim() ? draftProfile.phone : savedProfile.phone;
  // phoneVerified (from the backend) only ever describes savedProfile.phone — the backend
  // resets it to false the instant the on-file number changes (see repository.update_profile).
  // While editing, activePhoneRaw can be a not-yet-saved draft number, so the "Verified" badge
  // must only show when what's on screen still IS the verified number — otherwise a freshly
  // typed, never-verified number would inherit the old number's verified badge with no way to
  // verify it. Comparing normalized digits (not raw strings) so E.164 vs. plain-digit formatting
  // never causes a false mismatch.
  const isActivePhoneVerified = phoneVerified && formatUsCanadaPhone(activePhoneRaw) === formatUsCanadaPhone(savedProfile.phone);
  // "Resend" only unlocks once the currently-pending code has actually expired — true by
  // default when there's no pending code yet (0 <= 0), so the very first "Send Code"
  // click is never gated by this.
  const canResendEmail = emailOtpExpirySeconds <= 0;
  const canResendPhone = phoneOtpExpirySeconds <= 0;

  useEffect(() => {
    const nextProfile = profileToForm(backendProfile);
    setSavedProfile(nextProfile);
    if (!editing) setDraftProfile(nextProfile);
    setMemberSince(backendProfile.memberSince);
    // Just the status text ("Verification pending for X") — deliberately NOT touching
    // showEmailForm/showEmailOtp/newEmail here. This effect re-runs on every background
    // profile refresh (e.g. navigating back to this page), and forcing the OTP modal open
    // just because a pending email exists server-side was popping it up unprompted. The
    // modal should only ever open from the patient explicitly clicking "Change".
    setPendingEmail(backendProfile.pendingEmail);
    setPhoneVerified(backendProfile.phoneVerified);
  }, [backendProfile]);

  useEffect(() => {
    if (emailRetrySeconds <= 0) return;
    const timer = window.setInterval(() => {
      setEmailRetrySeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [emailRetrySeconds]);

  useEffect(() => {
    if (phoneRetrySeconds <= 0) return;
    const timer = window.setInterval(() => {
      setPhoneRetrySeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phoneRetrySeconds]);

  useEffect(() => {
    if (phoneOtpExpirySeconds <= 0) return;
    const timer = window.setInterval(() => {
      setPhoneOtpExpirySeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phoneOtpExpirySeconds]);

  useEffect(() => {
    if (emailOtpExpirySeconds <= 0) return;
    const timer = window.setInterval(() => {
      setEmailOtpExpirySeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [emailOtpExpirySeconds]);

  const updateDraft = (name: keyof ProfileForm, value: string) => {
    if (name === "phone") {
      setDraftProfile((current) => ({ ...current, phone: formatUsCanadaPhone(value) }));
      return;
    }
    setDraftProfile((current) => ({ ...current, [name]: value }));
  };

  const handleEditProfile = () => {
    setDraftProfile({
      ...savedProfile,
      firstName: "",
      lastName: "",
      address: "",
      phone: "",
      dateOfBirth: "",
    });
    setEditing(true);
  };

  const handleSaveChanges = async () => {
    if (!editing && !pendingAvatarFile) return;
    const profileToSave: ProfileForm = {
      ...savedProfile,
      ...draftProfile,
      firstName: draftProfile.firstName.trim() || savedProfile.firstName,
      lastName: draftProfile.lastName.trim() || savedProfile.lastName,
      address: draftProfile.address.trim() || savedProfile.address,
      phone: draftProfile.phone.trim() || savedProfile.phone,
      dateOfBirth: draftProfile.dateOfBirth || savedProfile.dateOfBirth,
      email: savedProfile.email,
    };

    if (!profileToSave.firstName.trim() || !profileToSave.lastName.trim()) {
      toastError("First name and last name are required.");
      return;
    }

    const phoneChanged = editing && draftProfile.phone.trim().length > 0;
    if (phoneChanged) {
      const e164Phone = toE164UsCanada(profileToSave.phone);
      if (!e164Phone) {
        toastError("Please enter a valid 10-digit U.S. or Canada phone number.");
        return;
      }
      profileToSave.phone = e164Phone;
    }

    const hasProfileFieldChanges =
      profileToSave.firstName !== savedProfile.firstName ||
      profileToSave.lastName !== savedProfile.lastName ||
      profileToSave.address !== savedProfile.address ||
      profileToSave.phone !== savedProfile.phone ||
      profileToSave.gender !== savedProfile.gender ||
      profileToSave.dateOfBirth !== savedProfile.dateOfBirth;

    if (editing && !hasProfileFieldChanges && !pendingAvatarFile) {
      toastWarning("No changes to save.");
      return;
    }

    setIsSavingProfile(true);
    let latestProfile: PatientProfile | null = null;
    let profileError: string | null = null;
    let avatarError: string | null = null;
    const wasEditingProfileFields = editing && hasProfileFieldChanges;
    const hadPendingAvatar = Boolean(pendingAvatarFile);

    // Profile-field update and avatar upload are independent operations — one failing
    // (e.g. a validation error on a field) must never block the other from being
    // attempted, and must never silently discard an already-selected image.
    if (wasEditingProfileFields) {
      const { email: _email, ...profilePayload } = profileToSave;
      const { data, error } = await updatePatientProfileApi(profilePayload);
      if (data) {
        latestProfile = data;
      } else {
        profileError = error ?? "Unable to update profile.";
      }
    }

    if (pendingAvatarFile) {
      const { data, error } = await uploadPatientAvatarApi(pendingAvatarFile);
      if (data) {
        latestProfile = data;
        setPendingAvatarFile(null);
        // The freshly-uploaded avatar now lives in `latestProfile.avatarUrl`; setProfile()
        // below propagates that into context, which re-fetches persistedAvatarSrc on its
        // own — drop the local blob preview so the display falls back to that shared value.
        if (localAvatarPreview) URL.revokeObjectURL(localAvatarPreview);
        setLocalAvatarPreview(null);
      } else {
        avatarError = error ?? "Unable to upload profile image.";
      }
    }

    setIsSavingProfile(false);

    if (latestProfile) {
      setProfile(latestProfile);
      const nextProfile = profileToForm(latestProfile);
      setSavedProfile(nextProfile);
      setDraftProfile(nextProfile);
      setMemberSince(latestProfile.memberSince);
      setPendingEmail(latestProfile.pendingEmail);
      setPhoneVerified(latestProfile.phoneVerified);
    }

    // Only leave edit mode if the profile-fields part either succeeded or was never
    // attempted — a failed field save keeps the form open so nothing typed is lost.
    if (!profileError) setEditing(false);

    if (profileError && avatarError) {
      toastError(`${profileError} Also: ${avatarError}`);
    } else if (profileError) {
      toastError(profileError);
    } else if (avatarError) {
      toastError(avatarError);
    } else if (wasEditingProfileFields && hadPendingAvatar) {
      toastSuccess("Profile and image updated.");
    } else if (hadPendingAvatar) {
      toastSuccess("Profile image updated.");
    } else if (wasEditingProfileFields) {
      toastSuccess("Profile updated.");
    }
  };

  const handleCancel = () => {
    setDraftProfile(savedProfile);
    setEditing(false);
    setPendingAvatarFile(null);
    if (localAvatarPreview) URL.revokeObjectURL(localAvatarPreview);
    setLocalAvatarPreview(null);
    // Abandoning the edit reverts the phone field too — a verify panel left open would
    // otherwise keep showing a code sent to a number the patient just discarded.
    setShowPhoneForm(false);
    setShowPhoneOtp(false);
    setPhoneOtp("");
    setPhoneOtpExpirySeconds(0);
    setVerifyingPhone(null);
  };

  const handlePasswordUpdate = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toastError("Please complete all password fields.");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toastError("New password must be at least 6 characters.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toastError("New passwords do not match.");
      return;
    }

    setIsSavingPassword(true);
    const { error } = await changePatientPasswordApi({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
      confirmPassword: passwordForm.confirmPassword,
    });
    setIsSavingPassword(false);

    if (error) {
      toastError(error);
      return;
    }

    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    toastSuccess("Password updated.");
  };

  const handleRequestEmailChange = async () => {
    // Captured before any state changes below — true only when this call came from the
    // password-step "Send Code"/"Resend Code" button (showEmailOtp is still false there).
    // The OTP-screen's own "Resend code" link always has showEmailOtp already true, so it
    // always falls through to a real backend call further down, never the resume branch.
    const isFromPasswordStep = !showEmailOtp;

    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      toastError("Please enter a new email address.");
      return;
    }
    if (trimmedEmail.toLowerCase() === savedProfile.email.toLowerCase()) {
      toastError("New email must be different from your current email.");
      return;
    }
    if (!emailChangePassword) {
      // Can happen if the OTP genuinely expires mid-session and the patient hits "Resend" —
      // the password field only lives on the Change Email panel, not the OTP modal, so send
      // them back there instead of leaving them stuck on a modal with no way to satisfy this.
      setShowEmailOtp(false);
      toastWarning("Please re-enter your current password to continue.");
      return;
    }

    if (isFromPasswordStep) {
      const resumable = getResumableEmailChangeSession(savedProfile.email, trimmedEmail);
      const passwordUnchanged =
        resumableEmailChangePassword !== null && emailChangePassword === resumableEmailChangePassword;
      if (resumable && passwordUnchanged) {
        // Same target email, same password, and the previously-sent code is still valid —
        // resume that session instead of sending (and thereby invalidating) a fresh code.
        setPendingEmail(trimmedEmail);
        setNewEmail(trimmedEmail);
        setShowEmailOtp(true);
        setEmailOtpExpirySeconds(resumable.remainingSeconds);
        return;
      }
    }

    setIsRequestingEmail(true);
    const result = await requestPatientEmailChangeApi(trimmedEmail, emailChangePassword);
    setIsRequestingEmail(false);

    if (!result.data) {
      if (result.retryAfterSeconds) setEmailRetrySeconds(result.retryAfterSeconds);
      toastError(result.error ?? "Unable to send verification code.");
      return;
    }

    setPendingEmail(trimmedEmail);
    setNewEmail(trimmedEmail);
    // Deliberately NOT clearing emailChangePassword here — "Resend code" inside the
    // OTP modal calls this same function again, and the password field only lives in
    // the (now-hidden) Change Email panel, so clearing it here would leave the patient
    // stuck with no way to re-enter it. It's cleared once the flow actually ends,
    // in closeEmailModal / handleVerifyEmailChange's success path.
    setEmailOtp("");
    setShowEmailOtp(true);
    const expiresInSeconds = result.data.expiresInSeconds ?? OTP_VALIDITY_SECONDS;
    setEmailOtpExpirySeconds(expiresInSeconds);
    setResumableEmailChangePassword(emailChangePassword);
    saveEmailChangeFlowState({
      accountEmail: savedProfile.email,
      pendingEmail: trimmedEmail,
      otpExpiresAt: Date.now() + expiresInSeconds * 1000,
    });
    toastSuccess(result.data.message);
  };

  const handleVerifyEmailChange = async () => {
    if (emailOtp.length !== OTP_LENGTH || emailOtpExpirySeconds <= 0) return;

    setIsVerifyingEmail(true);
    const result = await verifyPatientEmailChangeApi(emailOtp);
    setIsVerifyingEmail(false);

    if (!result.data) {
      if (result.retryAfterSeconds) setEmailRetrySeconds(result.retryAfterSeconds);
      toastError(result.error ?? "Unable to verify email code.");
      return;
    }

    const verifiedProfile = result.data;
    const nextProfile = profileToForm(verifiedProfile);
    setProfile(verifiedProfile);
    setSavedProfile(nextProfile);
    // Only the email actually changed on the backend — if the patient is mid-edit with
    // other unsaved changes (name, address, DOB, etc.), don't discard them by
    // overwriting the whole draft with the server's otherwise-unchanged profile.
    setDraftProfile((current) => (editing ? current : nextProfile));
    setPendingEmail(verifiedProfile.pendingEmail);
    setMemberSince(verifiedProfile.memberSince);
    setEmailOtp("");
    setNewEmail("");
    setEmailChangePassword("");
    setShowEmailForm(false);
    setShowEmailOtp(false);
    setEmailOtpExpirySeconds(0);
    setResumableEmailChangePassword(null);
    clearEmailChangeFlowState();
    usePatientAuthStore.setState((state) => ({
      user: state.user ? { ...state.user, email: verifiedProfile.email } : state.user,
    }));
    toastSuccess("Email address updated.");
  };

  const handleRequestPhoneVerification = async () => {
    const e164Phone = toE164UsCanada(newPhoneNumber);
    if (!e164Phone) {
      toastError("Please enter a valid U.S. or Canada phone number first.");
      return;
    }

    setIsRequestingPhone(true);
    const result = await requestPatientPhoneVerificationApi(e164Phone);
    setIsRequestingPhone(false);

    if (!result.data) {
      if (result.retryAfterSeconds) setPhoneRetrySeconds(result.retryAfterSeconds);
      toastError(result.error ?? "Unable to send verification code.");
      return;
    }

    setPhoneOtp("");
    setVerifyingPhone(e164Phone);
    setShowPhoneOtp(true);
    setPhoneOtpExpirySeconds(result.data.expiresInSeconds);
    toastSuccess(result.data.message);
  };

  const handleVerifyPhone = async () => {
    if (phoneOtp.length !== OTP_LENGTH || !verifyingPhone || phoneOtpExpirySeconds <= 0) return;

    setIsVerifyingPhone(true);
    const result = await verifyPatientPhoneVerificationApi(verifyingPhone, phoneOtp);
    setIsVerifyingPhone(false);

    if (!result.data) {
      if (result.retryAfterSeconds) setPhoneRetrySeconds(result.retryAfterSeconds);
      toastError(result.error ?? "Unable to verify phone code.");
      return;
    }

    const verifiedProfile = result.data;
    const nextProfile = profileToForm(verifiedProfile);
    setProfile(verifiedProfile);
    setSavedProfile(nextProfile);
    // Only the phone number actually changed on the backend — if the patient is
    // mid-edit with other unsaved changes (name, address, DOB, etc.), don't discard
    // them by overwriting the whole draft with the server's otherwise-unchanged
    // profile. Do still sync the phone field itself, since it just got verified.
    setDraftProfile((current) => (editing ? { ...current, phone: nextProfile.phone } : nextProfile));
    setPhoneVerified(verifiedProfile.phoneVerified);
    setPhoneOtp("");
    setNewPhoneNumber("");
    setVerifyingPhone(null);
    setShowPhoneForm(false);
    setShowPhoneOtp(false);
    setPhoneOtpExpirySeconds(0);
    toastSuccess("Phone number verified.");
  };

  const closePhoneModal = () => {
    setShowPhoneForm(false);
    setShowPhoneOtp(false);
    setPhoneOtp("");
    setPhoneOtpExpirySeconds(0);
    setPhoneConsent(false);
  };

  const closeEmailModal = () => {
    // UI teardown only — deliberately does NOT clear the persisted OTP session
    // (emailChangeFlowState / resumableEmailChangePassword). Closing this modal just
    // dismisses it; the still-active OTP must survive so re-opening "Change" and
    // re-submitting the same email+password can resume it instead of sending a new one.
    setShowEmailForm(false);
    setShowEmailOtp(false);
    setEmailOtp("");
    setEmailChangePassword("");
    setEmailOtpExpirySeconds(0);
  };

  const handleDeleteProfile = async () => {
    const confirmed = window.confirm("Delete this profile? This action cannot be undone.");
    if (!confirmed) return;

    const { error } = await deletePatientAccountApi();
    if (error) {
      toastError(error);
      return;
    }

    toastSuccess("Profile deleted.");
    await signOut();
  };

  const handleAvatarChange = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toastError("Please choose an image file.");
      return false;
    }

    if (localAvatarPreview) URL.revokeObjectURL(localAvatarPreview);
    setLocalAvatarPreview(URL.createObjectURL(file));
    setPendingAvatarFile(file);
    toastSuccess("Profile image selected. Save changes to upload it.");
    return false;
  };

  // A pending local file selection (blob: URL, no network fetch needed) takes priority
  // over the persisted avatar shared from context — falls back to that once there's no
  // pending selection (initial load, or right after a save clears the local preview).
  const avatarDisplaySrc = localAvatarPreview ?? persistedAvatarSrc;

  return (
    <div className="mx-auto w-full max-w-[1120px] px-8 py-8">
      <section className="rounded-lg border border-[#E5E7EB] bg-white p-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-6">
            <div className="relative h-24 w-24 shrink-0">
              <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#180047] text-3xl font-semibold !text-white">
                {avatarDisplaySrc ? (
                  <img src={avatarDisplaySrc} alt="" className="h-full w-full object-cover" />
                ) : (
                  getInitials(savedProfile.firstName, savedProfile.lastName)
                )}
              </div>
              {pendingAvatarFile && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/35 text-[10px] font-semibold text-white">
                  Pending
                </div>
              )}
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleAvatarChange}>
                <div
                  className="absolute -bottom-1 -right-1 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-[3px] border-white bg-[#05010F] text-white shadow-sm transition-opacity hover:opacity-90"
                  role="button"
                  tabIndex={0}
                  aria-label="Choose profile image"
                >
                  <Icon icon="lucide:camera" className="icon-muted h-4 w-4" />
                </div>
              </Upload>
            </div>

            <div>
              <h1 className="text-2xl font-extrabold text-[#111827]">{fullName}</h1>
              <p className="mt-1 text-sm text-[#6B7280]">{savedProfile.email}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  onClick={handleEditProfile}
                  disabled={isSavingProfile}
                  className={`!h-auto !rounded-lg !px-5 !py-2.5 !text-sm !font-semibold !text-white ${BRAND_RED_SOFT}`}
                >
                  Edit Profile
                </Button>
                <Button
                  onClick={() => setActiveTab("security")}
                  className={`!h-auto !rounded-lg !px-5 !py-2.5 !text-sm !font-semibold !text-white ${BRAND_RED_SOFT}`}
                >
                  Change Password
                </Button>
              </div>
            </div>
          </div>

          <div className="text-left md:text-right">
            <p className="text-sm text-[#6B7280]">Member since</p>
            <p className="mt-1 text-base font-semibold text-[#111827]">{formatMonthYear(memberSince)}</p>
          </div>
        </div>
      </section>

      <section className="mt-6 overflow-visible rounded-lg border border-[#E5E7EB] bg-white">
        <div className="flex border-b border-[#E5E7EB] px-6">
          <Button
            variant="ghost"
            onClick={() => setActiveTab("personal")}
            className={cn(
              "!h-auto !rounded-none !border-0 !bg-transparent !px-2 !py-4 !text-sm !font-semibold !shadow-none !transition-transform hover:!bg-transparent active:!bg-transparent",
              activeTab === "personal"
                ? "!-translate-y-0.5 !scale-[1.03] !font-bold !text-[#180047] [text-shadow:0_2px_3px_rgba(24,0,71,0.16)]"
                : "!text-[#6B7280] hover:!text-[#111827]"
            )}
          >
            Personal Info
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab("security")}
            className={cn(
              "!ml-6 !h-auto !rounded-none !border-0 !bg-transparent !px-2 !py-4 !text-sm !font-semibold !shadow-none !transition-transform hover:!bg-transparent active:!bg-transparent",
              activeTab === "security"
                ? "!-translate-y-0.5 !scale-[1.03] !font-bold !text-[#180047] [text-shadow:0_2px_3px_rgba(24,0,71,0.16)]"
                : "!text-[#6B7280] hover:!text-[#111827]"
            )}
          >
            Security
          </Button>
        </div>

        <div className="p-6">
          {activeTab === "personal" && (
          <>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            <ProfileField icon={<Icon icon="lucide:user-round" className="icon-muted h-4 w-4" />} label="First Name" name="firstName" value={editing ? draftProfile.firstName : savedProfile.firstName} placeholderValue={savedProfile.firstName} editing={editing} onChange={updateDraft} />
            <ProfileField icon={<Icon icon="lucide:user-round" className="icon-muted h-4 w-4" />} label="Last Name" name="lastName" value={editing ? draftProfile.lastName : savedProfile.lastName} placeholderValue={savedProfile.lastName} editing={editing} onChange={updateDraft} />
            <ProfileField icon={<Icon icon="lucide:map-pin" className="icon-muted h-4 w-4" />} label="Address" name="address" value={editing ? draftProfile.address : savedProfile.address} placeholderValue={savedProfile.address || "Address"} editing={editing} onChange={updateDraft} wide />
            <ProfileField
              icon={<Icon icon="lucide:phone" className="icon-muted h-4 w-4" />}
              label="Phone Number"
              name="phone"
              value={editing ? draftProfile.phone : formatUsCanadaPhone(savedProfile.phone)}
              placeholderValue={formatUsCanadaPhone(savedProfile.phone) || "Phone number"}
              editing={editing}
              type="tel"
              onChange={updateDraft}
              hasValue={Boolean(activePhoneRaw.trim())}
              prefix={
                <>
                  <Select
                    variant="borderless"
                    value={phoneCountry}
                    onChange={(next) => setPhoneCountry(next as "US" | "CA")}
                    className="!mr-2 !shrink-0 !text-sm !font-medium !text-[#111827]"
                    popupMatchSelectWidth={false}
                    options={[
                      { value: "US", label: "🇺🇸 +1" },
                      { value: "CA", label: "🇨🇦 +1" },
                    ]}
                  />
                  <div className="mr-2 h-5 w-px shrink-0 bg-[#DADDE5]" />
                </>
              }
              suffix={
                isActivePhoneVerified ? (
                  <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-[#16A34A] px-3 py-1 text-xs font-semibold !text-white">
                    Verified
                  </span>
                ) : (
                  <Button
                    onClick={() => {
                      setNewPhoneNumber(activePhoneRaw);
                      setPhoneConsent(false);
                      setShowPhoneForm(true);
                      setShowPhoneOtp(false);
                      setPhoneOtp("");
                    }}
                    className="!ml-2 !h-auto !inline-flex !shrink-0 !items-center !rounded-full !px-3 !py-1 !text-xs !font-semibold !text-white !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60"
                  >
                    Verify
                  </Button>
                )
              }
              emptyAction={
                <Button
                  onClick={() => {
                    setNewPhoneNumber("");
                    setPhoneConsent(false);
                    setShowPhoneForm(true);
                    setShowPhoneOtp(false);
                    setPhoneOtp("");
                  }}
                  className={`!h-auto !rounded-full !px-4 !py-1.5 !text-xs !font-semibold !text-white ${BRAND_RED_SOFT}`}
                >
                  Add Phone Number
                </Button>
              }
            />
            <ProfileField
              icon={<Icon icon="lucide:mail" className="icon-muted h-4 w-4" />}
              label="Email Address"
              name="email"
              value={savedProfile.email}
              editing={false}
              type="email"
              onChange={updateDraft}
              suffix={
                <Button
                  onClick={() => {
                    // Always open at the password step — never jump straight to the OTP
                    // modal just because a pendingEmail already exists server-side. That
                    // shortcut let anyone with access to an open, authenticated tab reach
                    // the OTP-entry screen without ever supplying the account password.
                    setShowEmailForm(true);
                    setShowEmailOtp(false);
                    setNewEmail(pendingEmail ?? "");
                    setEmailChangePassword("");
                  }}
                  className="!ml-2 !h-auto !inline-flex !shrink-0 !items-center !rounded-full !px-3 !py-1 !text-xs !font-semibold !text-white !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60"
                >
                  Change
                </Button>
              }
            />
            <ProfileField icon={<Icon icon="lucide:user-round" className="icon-muted h-4 w-4" />} label="Gender" name="gender" value={editing ? draftProfile.gender : savedProfile.gender} editing={editing} type="select" onChange={updateDraft} />
            <ProfileField icon={null} label="Date of Birth" name="dateOfBirth" value={editing ? draftProfile.dateOfBirth : savedProfile.dateOfBirth} placeholderValue="Date of birth" editing={editing} type="date" onChange={updateDraft} />
          </div>

          <AppModal
            open={showEmailForm && !showEmailOtp}
            onOpenChange={(open) => { if (!open) closeEmailModal(); }}
            width={384}
            hideCloseButton
            className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
          >
            <div className="relative">
              <Button
                variant="ghost"
                onClick={closeEmailModal}
                aria-label="Close"
                className="!absolute !right-0 !top-0 !flex !h-7 !w-7 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-black/5 hover:!text-[#111827]"
              >
                <Icon icon="lucide:x" className="icon-muted h-4 w-4" />
              </Button>

              <div className="flex items-start gap-3 pr-8">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${BRAND_RED_SOFT}`}>
                  <Icon icon="lucide:mail" className="h-5 w-5 !text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#180047]">Change Email</h2>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    Current email: <span className="font-semibold text-[#111827]">{savedProfile.email}</span>
                    {pendingEmail && (
                      <>
                        {" "}· Verification pending for <span className="font-semibold text-[#E4002B]">{pendingEmail}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-3">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  placeholder="New email address"
                  className="!h-11 !rounded-lg !border-[#E5E7EB] !px-3 !text-sm focus:!border-[#E4002B]/50"
                />
                <AntInput.Password
                  value={emailChangePassword}
                  onChange={(event) => setEmailChangePassword(event.target.value)}
                  placeholder="Current password"
                  visibilityToggle={{ visible: showEmailChangePassword, onVisibleChange: setShowEmailChangePassword }}
                  className="!h-11 !w-full !rounded-lg !border-[#E5E7EB] !px-3 !text-sm focus:!border-[#E4002B]/50"
                />
              </div>

              <Button
                htmlType="button"
                onClick={handleRequestEmailChange}
                loading={isRequestingEmail}
                disabled={isRequestingEmail || emailRetrySeconds > 0 || !canResendEmail}
                className={`!mt-5 !h-auto !w-full !rounded-full !border-none !py-3 !text-sm !font-semibold !text-white !transition-opacity hover:!opacity-90 disabled:!opacity-50 !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60`}
              >
                {isRequestingEmail
                  ? "Sending..."
                  : emailRetrySeconds > 0
                    ? `Resend in ${emailRetrySeconds}s`
                    : pendingEmail
                      ? "Resend Code"
                      : "Send Code"}
              </Button>
            </div>
          </AppModal>

          <AppModal
            open={showEmailForm && showEmailOtp}
            onOpenChange={(open) => { if (!open) closeEmailModal(); }}
            width={384}
            hideCloseButton
            className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
          >
            <div className="relative">
              <Button
                variant="ghost"
                onClick={closeEmailModal}
                aria-label="Close"
                className="!absolute !right-0 !top-0 !flex !h-7 !w-7 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-black/5 hover:!text-[#111827]"
              >
                <Icon icon="lucide:x" className="icon-muted h-4 w-4" />
              </Button>

              <div className="flex items-start gap-3 pr-8">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${BRAND_RED_SOFT}`}>
                  <Icon icon="lucide:mail" className="h-5 w-5 !text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#180047]">Enter Code</h2>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    We've sent a 6-digit code to {pendingEmail ?? newEmail}. Enter the code below to continue.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center">
                <p className="text-sm font-medium text-[#6B7280]">Verification Code</p>
                <div className="mt-3">
                  <InputOTP
                    length={OTP_LENGTH}
                    value={emailOtp}
                    onChange={setEmailOtp}
                    disabled={isVerifyingEmail || emailOtpExpirySeconds <= 0}
                    containerClassName="[&_.ant-otp-input:not([value=''])]:!bg-[#F36F8F] [&_.ant-otp-input:not([value=''])]:!text-white [&_.ant-otp-input:not([value=''])]:!border-[#F36F8F]"
                  />
                </div>
                <p className="mt-3 text-center text-xs text-[#6B7280]">
                  {emailOtpExpirySeconds > 0 ? (
                    <>
                      Code expires in{" "}
                      <span className="font-semibold text-[#111827]">{formatMinutesSeconds(emailOtpExpirySeconds)}</span>
                    </>
                  ) : (
                    <span className={`font-semibold ${BRAND_RED_TEXT}`}>Code expired.</span>
                  )}
                  {" · "}
                  <Button
                    variant="link"
                    onClick={handleRequestEmailChange}
                    disabled={isRequestingEmail || emailRetrySeconds > 0 || !canResendEmail}
                    className="!h-auto !p-0 !font-semibold !text-[#E4002B] hover:!underline disabled:!text-[#9CA3AF] disabled:!no-underline"
                  >
                    {emailRetrySeconds > 0 ? `Resend in ${emailRetrySeconds}s` : "Resend code"}
                  </Button>
                </p>
              </div>

              <Button
                htmlType="button"
                onClick={handleVerifyEmailChange}
                loading={isVerifyingEmail}
                disabled={isVerifyingEmail || emailOtp.length !== OTP_LENGTH || emailOtpExpirySeconds <= 0}
                className={`!mt-5 !h-auto !w-full !rounded-full !border-none !py-3 !text-sm !font-semibold !text-white !transition-opacity hover:!opacity-90 disabled:!opacity-50 !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60`}
              >
                {isVerifyingEmail ? "Verifying..." : "Verify Code"}
              </Button>
            </div>
          </AppModal>

          <AppModal
            open={showPhoneForm && !showPhoneOtp}
            onOpenChange={(open) => { if (!open) closePhoneModal(); }}
            width={384}
            hideCloseButton
            className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
          >
            <div className="relative">
              <Button
                variant="ghost"
                onClick={closePhoneModal}
                aria-label="Close"
                className="!absolute !right-0 !top-0 !flex !h-7 !w-7 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-black/5 hover:!text-[#111827]"
              >
                <Icon icon="lucide:x" className="icon-muted h-4 w-4" />
              </Button>

              <div className="flex items-start gap-3 pr-8">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${BRAND_RED_SOFT}`}>
                  <Icon icon="lucide:phone" className="h-5 w-5 !text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#180047]">Verify Contact Information</h2>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    We'll send a 6-digit verification code via SMS to your phone number.
                  </p>
                </div>
              </div>

              <div className="mt-5">
                <span className="mb-2 flex items-center gap-2 text-sm font-medium text-[#111827]">
                  <Icon icon="lucide:phone" className="icon-muted h-4 w-4" />
                  Phone Number
                </span>
                <div className="flex h-11 items-center gap-2 rounded-lg bg-[#F1F2F4] px-3">
                  <Select
                    variant="borderless"
                    value={phoneCountry}
                    onChange={(next) => setPhoneCountry(next as "US" | "CA")}
                    className="!shrink-0 !text-sm !font-medium !text-[#111827]"
                    popupMatchSelectWidth={false}
                    options={[
                      { value: "US", label: "🇺🇸 +1" },
                      { value: "CA", label: "🇨🇦 +1" },
                    ]}
                  />
                  <div className="h-5 w-px shrink-0 bg-[#DADDE5]" />
                  <Input
                    variant="borderless"
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(event) => setNewPhoneNumber(formatUsCanadaPhone(event.target.value))}
                    placeholder="(555) 123-4567"
                    className={`!min-w-0 !flex-1 !p-0 !text-sm !font-semibold placeholder:!font-normal placeholder:!text-[#7A7F8D] ${BRAND_RED_TEXT}`}
                  />
                </div>
              </div>

              <label className="mt-4 flex cursor-pointer items-start gap-2 text-xs text-[#6B7280]">
                <Checkbox
                  checked={phoneConsent}
                  onChange={(event) => setPhoneConsent(event.target.checked)}
                  className="[&_.ant-checkbox-inner]:!rounded [&_.ant-checkbox-inner]:!border-[#F36F8F] [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-[#F36F8F] [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-[#F36F8F]"
                />
                <span>I consent to receive a one-time verification code via SMS to verify my phone number.</span>
              </label>

              <Button
                htmlType="button"
                onClick={handleRequestPhoneVerification}
                loading={isRequestingPhone}
                disabled={isRequestingPhone || phoneRetrySeconds > 0 || !canResendPhone || !newPhoneNumber.trim() || !phoneConsent}
                className={`!mt-5 !h-auto !w-full !rounded-full !border-none !py-3 !text-sm !font-semibold !text-white !transition-opacity hover:!opacity-90 disabled:!opacity-50 !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60`}
              >
                {isRequestingPhone
                  ? "Sending..."
                  : phoneRetrySeconds > 0
                    ? `Resend in ${phoneRetrySeconds}s`
                    : "Send Code"}
              </Button>
            </div>
          </AppModal>

          <AppModal
            open={showPhoneForm && showPhoneOtp}
            onOpenChange={(open) => { if (!open) closePhoneModal(); }}
            width={384}
            hideCloseButton
            className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-6"
          >
            <div className="relative">
              <Button
                variant="ghost"
                onClick={closePhoneModal}
                aria-label="Close"
                className="!absolute !right-0 !top-0 !flex !h-7 !w-7 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-black/5 hover:!text-[#111827]"
              >
                <Icon icon="lucide:x" className="icon-muted h-4 w-4" />
              </Button>

              <div className="flex items-start gap-3 pr-8">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${BRAND_RED_SOFT}`}>
                  <Icon icon="lucide:phone" className="h-5 w-5 !text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#180047]">Enter Code</h2>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    We've sent a 6-digit code to {formatUsCanadaPhone(verifyingPhone)}. Enter the code below to continue.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center">
                <p className="text-sm font-medium text-[#6B7280]">Verification Code</p>
                <div className="mt-3">
                  <InputOTP
                    length={OTP_LENGTH}
                    value={phoneOtp}
                    onChange={setPhoneOtp}
                    disabled={isVerifyingPhone || phoneOtpExpirySeconds <= 0}
                    containerClassName="[&_.ant-otp-input:not([value=''])]:!bg-[#F36F8F] [&_.ant-otp-input:not([value=''])]:!text-white [&_.ant-otp-input:not([value=''])]:!border-[#F36F8F]"
                  />
                </div>
                <p className="mt-3 text-center text-xs text-[#6B7280]">
                  {phoneOtpExpirySeconds > 0 ? (
                    <>
                      Code expires in{" "}
                      <span className="font-semibold text-[#111827]">{formatMinutesSeconds(phoneOtpExpirySeconds)}</span>
                    </>
                  ) : (
                    <span className={`font-semibold ${BRAND_RED_TEXT}`}>Code expired.</span>
                  )}
                  {" · "}
                  <Button
                    variant="link"
                    onClick={handleRequestPhoneVerification}
                    disabled={isRequestingPhone || phoneRetrySeconds > 0 || !canResendPhone}
                    className="!h-auto !p-0 !font-semibold !text-[#E4002B] hover:!underline disabled:!text-[#9CA3AF] disabled:!no-underline"
                  >
                    {phoneRetrySeconds > 0 ? `Resend in ${phoneRetrySeconds}s` : "Resend code"}
                  </Button>
                </p>
              </div>

              <Button
                htmlType="button"
                onClick={handleVerifyPhone}
                loading={isVerifyingPhone}
                disabled={isVerifyingPhone || phoneOtp.length !== OTP_LENGTH || phoneOtpExpirySeconds <= 0}
                className={`!mt-5 !h-auto !w-full !rounded-full !border-none !py-3 !text-sm !font-semibold !text-white !transition-opacity hover:!opacity-90 disabled:!opacity-50 !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60`}
              >
                {isVerifyingPhone ? "Verifying..." : "Verify Code"}
              </Button>
            </div>
          </AppModal>

          <div className="mt-6 flex flex-col gap-6 border-t border-[#E5E7EB] pt-5">
            <h2 className={`text-base font-semibold ${BRAND_RED_TEXT}`}>Danger Zone</h2>
            <p className="text-sm text-[#6B7280]">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <Button
              onClick={handleDeleteProfile}
              className={`!h-auto !self-start !inline-flex !items-center !gap-2 !rounded-lg !px-5 !py-2.5 !text-sm !font-semibold !text-white ${BRAND_RED_SOFT}`}
            >
              Delete Account
            </Button>
          </div>
          </>
          )}

          {activeTab === "security" && (
            <div>
              <h2 className="text-lg font-bold text-[#180047]">Change Password</h2>
              <div className="mt-5 grid gap-4">
                <PasswordField
                  placeholder="Current Password"
                  value={passwordForm.currentPassword}
                  onChange={(value) => setPasswordForm((current) => ({ ...current, currentPassword: value }))}
                  visible={showCurrentPassword}
                  onToggleVisible={() => setShowCurrentPassword((visible) => !visible)}
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <PasswordField
                    placeholder="New Password"
                    value={passwordForm.newPassword}
                    onChange={(value) => setPasswordForm((current) => ({ ...current, newPassword: value }))}
                    visible={showNewPassword}
                    onToggleVisible={() => setShowNewPassword((visible) => !visible)}
                  />
                  <PasswordField
                    placeholder="Confirm New Password"
                    value={passwordForm.confirmPassword}
                    onChange={(value) => setPasswordForm((current) => ({ ...current, confirmPassword: value }))}
                    visible={showConfirmPassword}
                    onToggleVisible={() => setShowConfirmPassword((visible) => !visible)}
                  />
                </div>
              </div>

              <div className="mt-6 border-t border-[#E5E7EB] pt-6">
                <h2 className="text-lg font-bold text-[#180047]">Two-Factor Authentication</h2>
                <p className="mt-1 text-sm text-[#6B7280]">Add an extra layer of security to your account.</p>
                <Button
                  className={`!h-auto !mt-4 !rounded-full !px-6 !py-2.5 !text-sm !font-semibold !text-white ${BRAND_RED_SOFT}`}
                >
                  Enable 2FA
                </Button>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-[#E5E7EB] pt-5">
                <Button
                  variant="outline"
                  onClick={() => setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })}
                  className="!h-auto !rounded-full !border-none !bg-[#EEF0F5] !px-6 !py-2.5 !text-sm !font-semibold !text-[#111827] hover:!bg-[#E5E7EB] hover:!text-[#111827] active:!bg-[#DDE1E8]"
                >
                  Cancel
                </Button>
                <Button
                  htmlType="button"
                  onClick={handlePasswordUpdate}
                  loading={isSavingPassword}
                  disabled={isSavingPassword}
                  className={`!h-auto !rounded-full !border-none !px-6 !py-2.5 !text-sm !font-semibold !text-white !transition-colors disabled:!opacity-50 !bg-[#E4002B]/50 hover:!bg-[#E4002B]/60`}
                >
                  {isSavingPassword ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {activeTab === "personal" && (
        <div className="flex justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={!editing}
            className="!h-auto !rounded-lg !border-none !bg-[#EEF0F5] !px-6 !py-2.5 !text-sm !font-semibold !text-[#111827] hover:!bg-[#E5E7EB] hover:!text-[#111827] active:!bg-[#DDE1E8]"
          >
            Cancel
          </Button>
          <Button
            htmlType="button"
            onClick={handleSaveChanges}
            loading={isSavingProfile}
            disabled={(!editing && !pendingAvatarFile) || isSavingProfile}
            className={`!h-auto !rounded-lg !border-none !px-6 !py-2.5 !text-sm !font-semibold !transition-colors disabled:!opacity-50 !bg-[#E4002B]/20 !text-[#E4002B] hover:!bg-[#E4002B]/25`}
          >
            {isSavingProfile ? "Saving..." : "Save Changes"}
          </Button>
        </div>
        )}
      </section>
    </div>
  );
}
